import { apiClient } from './client';

// ── Tipos ──────────────────────────────────────────────────────────────────

export type AiProviderStatus =
  | 'ok' | 'rate_limited' | 'no_credits' | 'error' | 'unconfigured' | 'disabled' | 'timeout';

export type TelegramBotStatus =
  | 'ok' | 'privacy_on' | 'no_webhook' | 'error' | 'unconfigured' | 'timeout';

export interface ProviderCheck {
  status:     AiProviderStatus;
  latencyMs?: number;
  message?:   string;
  checkedAt:  string;
}

export interface TelegramBotCheck {
  status:          TelegramBotStatus;
  username?:       string;
  canReadGroups?:  boolean;
  webhookUrl?:     string;
  pendingUpdates?: number;
  lastError?:      string;
  latencyMs?:      number;
  checkedAt:       string;
}

export interface SystemStatus {
  providers: {
    gemini:    ProviderCheck;
    openai:    ProviderCheck;
    anthropic: ProviderCheck;
    groq:      ProviderCheck;
    deepseek:  ProviderCheck;
    mistral?:  ProviderCheck;
    cerebras?:   ProviderCheck;
    openrouter?: ProviderCheck;
    cloudflare?: ProviderCheck;
  };
  /** Modelo real configurado por proveedor (backend config/ai-models.ts). */
  models?: Partial<Record<string, string>>;
  telegram: {
    fieldBot:   TelegramBotCheck;
  };
  cachedAt:   string;
  fresh:      boolean;
  ageSeconds: number;
}

// ── Fetch ──────────────────────────────────────────────────────────────────

export const fetchSystemStatus = async (): Promise<SystemStatus> =>
  (await apiClient.get('/api/admin/ai-status')).data;

// ── Uso de IA por modelo ───────────────────────────────────────────────────

export interface AiUsageModelRow {
  provider: string;
  model: string;
  ok: number;
  failed: number;
  calls: number;
  avgLatencyMs: number;
  sharePct: number;
  tokensIn: number;
  tokensOut: number;
  failuresByCause: Record<string, number>;
}

export interface AiUsageSummary {
  days: number;
  from: string;
  to: string;
  totals: { ok: number; failed: number; calls: number };
  byModel: AiUsageModelRow[];
  failuresByCause: Record<string, number>;
}

export const fetchAiUsage = async (days: 7 | 30): Promise<AiUsageSummary> =>
  (await apiClient.get('/api/admin/ai-usage', { params: { days } })).data;

export interface ChatFeedbackCount {
  from: string;
  to: string;
  up: number;
  down: number;
  total: number;
}

/** Conteo de 👍/👎 del chat web de la empresa entre dos días (YYYY-MM-DD, ambos incluidos). */
export const fetchChatFeedbackCount = async (from: string, to: string): Promise<ChatFeedbackCount> =>
  (await apiClient.get('/api/admin/chat-feedback', { params: { from, to } })).data;

// ── Rotación de claves y tokens (SUPER_ADMIN) ──────────────────────────────

export interface RotatableSecret { key: string; label: string; configured: boolean }

export type RotateSecretResult =
  | { ok: true; key: string; username?: string; webhookUrl?: string; pendingUpdates?: number; lastError?: string }
  | { ok: false; code: string; message: string };

/** Claves que se pueden rotar y si están configuradas (nunca los valores). */
export const fetchRotatableSecrets = async (): Promise<RotatableSecret[]> =>
  (await apiClient.get('/api/admin/secrets')).data.items ?? [];

/** Guarda el valor nuevo en el servidor (el del bot de Campo además lo valida y re-registra el webhook). 4xx/5xx también devuelven el cuerpo. */
export const rotateSecret = async (key: string, value: string): Promise<RotateSecretResult> =>
  (await apiClient.post('/api/admin/secrets/rotate', { key, value }, { validateStatus: () => true })).data;
